import { Check, Clock, Copy, RotateCw, UserPlus, Wallet, X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaLogo } from "@/shared/components/ColocaLogo";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { TransferReceiptActions } from "@/shared/components/payments/TransferReceiptActions";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { DispersionSuccessData } from "../../hooks/useSubmitDispersion";
import { PayoutStatus } from "../../services/paymentsPayout.service";
import { quoteFeeView } from "../../services/paymentsQuotes.service";
import type { EnhancedCountry } from "../../types/dispersions-api.types";
import { formatDecimalAmount2, getCurrencySymbol } from "../../utils/currencyFormatter";
import { recipientAccountDraft } from "../../utils/recipientAccountDraft";
import { SaveRecipientAction } from "./SaveRecipientAction";

export interface PayoutReceiptProps {
  successData: DispersionSuccessData;
  /**
   * The payout's CURRENT status — the create response upgraded by the live channel (`usePayoutLiveStatus`).
   * Everything the receipt asserts is derived from it, so the document never claims the money arrived before
   * the provider says so.
   */
  payoutStatus: PayoutStatus | null;
  countries: EnhancedCountry[];
  showDetails: boolean;
  onToggleDetails: () => void;
  /** Repeat this exact send — same recipient, same amount. */
  onSendAgain: () => void;
  /** Same wallet, corridor and amount; a recipient still to be entered. */
  onSendToSomeoneElse: () => void;
  /** Leave the wizard for the wallets home. */
  onBackToWallets: () => void;
  /** Signs the comprobante while the organization has no identity of its own. */
  organizationName?: string | null;
  /** Drawn instead of the clock while the payout waits for the destination bank (the money on its way). */
  pendingVisual?: ReactNode;
}

/** One line of the stub. */
function StubRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

/** Which of the three things that can be true of a payout is true right now. */
type Outcome = "paid" | "pending" | "rejected";

function outcomeOf(status: PayoutStatus): Outcome {
  if (status === PayoutStatus.PAID) return "paid";
  if (
    status === PayoutStatus.FAILED ||
    status === PayoutStatus.CANCELLED ||
    status === PayoutStatus.REVERSED
  ) {
    return "rejected";
  }
  return "pending";
}

/**
 * What the wizard produces once the payout exists: a receipt.
 *
 * Not a success banner, because that shape can only say one of the three things that are actually true here.
 * A receipt is the artifact this flow really produces — the issuer's mark, the figure, who received it, the
 * reference — and the same document works stamped "entregado", "en camino" or "rechazado". It is also what
 * people do with this screen: they screenshot it and send it to the person who received the money, which is
 * the whole reason the Coloca mark is on it. A card with no issuer proves nothing once it leaves the app.
 *
 * **The three outcomes are one layout at three temperatures, never three screens.** The figure is always the
 * amount that was addressed to the recipient; the glyph, the headline, the pill and one sentence carry the
 * difference. Written the other way around — a celebration for `PAID` and a cut-down version for the rest —
 * the pending face inherits the celebration, and `PROCESSING` only means the provider accepted the payout. It
 * stays live while open (`usePayoutLiveStatus`), so this screen re-renders into a different temperature on
 * its own when the settlement webhook lands.
 */
export function PayoutReceipt({
  successData,
  payoutStatus,
  countries,
  showDetails,
  onToggleDetails,
  onSendAgain,
  onSendToSomeoneElse,
  onBackToWallets,
  organizationName,
  pendingVisual,
}: PayoutReceiptProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const taxLabel = useTaxLabel();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const repeatHintId = useId();
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The button that was focused is unmounted when the phase flips, dropping focus to <body> — so a screen
  // reader announced nothing at all when the payout resolved.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(
    () => () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    },
    [],
  );

  const originCurrency = successData.formData.origin_currency;
  const destination = countries.find((c) => c.code === successData.formData.destination);
  // Decimal strings straight from the frozen quote — the fallbacks cover the (unreachable) case of a payout
  // submitted without one.
  const send = successData.quoteData?.amounts.send ?? (successData.formData.amount_origin || "0");
  const fees = successData.quoteData ? quoteFeeView(successData.quoteData) : null;
  const received = fees?.beneficiaryReceives ?? (successData.formData.amount_destination || "0");
  // The receipt draws its own figures and the stub below renders `QuoteBreakdown`, which formats through
  // `ColocaAmountDisplay` — two formatters on one screen, so both read the reader's language and both group
  // unconditionally. Pinned to es-CO, this one wrote `1.500,50` above a stub writing `1,500.50` in English.
  const format = (value: string, currency: string) =>
    `${getCurrencySymbol(currency)} ${formatDecimalAmount2(value, activeLanguage)} ${currency}`.trim();

  const reference = successData.dispersionResponse.id;
  const concept = String(successData.formData.recipientFields?.description ?? "").trim() || "—";
  const status = payoutStatus ?? successData.dispersionResponse.status;
  const outcome = outcomeOf(status);
  // Repeating is held while the network has not answered — see the action row at the foot of this file.
  const repeatHeld = outcome === "pending";

  // Offered only for a destination that was TYPED and that an account can actually hold. A send to a client
  // already on file has nothing to save, and a corridor the account matrix cannot express (Transfiya) would
  // open a form nobody can complete.
  //
  // And never OFFERED after a REJECTED send. The whole value of saving is that the wizard autofills from it
  // next time, so writing an account the network just refused files a bad destination under a real client and
  // then offers it back as a known-good one. The receipt stays mounted and flips on the settlement webhook,
  // so this is not only the immediate-failure case: the offer has to withdraw itself when the outcome turns.
  //
  // **Withdrawing is a prop, not a null draft**, and the difference is the whole of a data-loss bug. Folding
  // the outcome into `saveDraft` unmounted `SaveRecipientAction` the instant a webhook turned a PROCESSING
  // payout into a FAILED one — and that subtree owns `isOpen` and `clientCreated`, so an operator halfway
  // through the create-client drawer lost everything typed with nothing on screen saying why, and a client
  // created a second earlier was left behind with no payable account: the exact state that component's own
  // comment calls worse than never offering. The draft now describes only what COULD be saved; whether to
  // keep offering it is the component's business, and it refuses to vanish out from under an open form.
  const saveDraft =
    successData.formData.recipientMode === "manual"
      ? recipientAccountDraft(successData.formData.destination, successData.formData.recipientFields)
      : null;

  type ChipVariant = React.ComponentProps<typeof ColocaStatusPill>["variant"];
  const chips: Record<string, { label: string; variant: ChipVariant }> = {
    [PayoutStatus.CREATED]: { label: t("result_status_created"), variant: "warning" },
    [PayoutStatus.PROCESSING]: { label: t("result_status_processing"), variant: "info" },
    [PayoutStatus.PAID]: { label: t("result_status_paid"), variant: "success" },
    [PayoutStatus.FAILED]: { label: t("result_status_failed"), variant: "danger" },
    [PayoutStatus.CANCELLED]: { label: t("result_status_cancelled"), variant: "danger" },
    [PayoutStatus.REVERSED]: { label: t("result_status_reversed"), variant: "danger" },
  };
  // A status the backend adds later must not take the receipt down AFTER the money has left. The live status
  // arrives over the socket and is cast, not validated (`usePayoutLiveStatus`), so an unknown value reaches
  // here intact; indexing this map without a fallback threw on `chip.variant`. Falls back the way the
  // activity screens do: the raw value, in a neutral tone. `outcomeOf` already degrades to `pending`.
  const chip = chips[status] ?? { label: status, variant: "info" as const };

  const headline = {
    paid: {
      icon: <Check className="size-7" strokeWidth={2.6} aria-hidden="true" />,
      ring: "bg-success/10 text-success",
      title: t("result_success_title"),
      note: t("result_success_sent_note"),
    },
    pending: {
      icon: <Clock className="size-7" strokeWidth={2.2} aria-hidden="true" />,
      ring: "bg-warning/10 text-warning",
      title: t("result_pending_title"),
      note:
        status === PayoutStatus.CREATED ? t("result_success_created_note") : t("result_pending_sent_note"),
    },
    rejected: {
      icon: <X className="size-6" strokeWidth={2.6} aria-hidden="true" />,
      ring: "bg-destructive/10 text-destructive",
      title: t("result_rejected_title"),
      // Where the money went back to is NOT the wallet it left, once a conversion happened. The runtime
      // releases the funds in the DELIVERY currency and keeps the conversion fee
      // (docs/payments-quote-flow.md § Money movement), so the organization ends up as if it had swapped.
      // The singular "your wallet" reads as the one they sent from, which is the one place it is not: an
      // operator checks USD, finds nothing, and opens a ticket about money that is sitting in COP.
      //
      // The currency comes from the QUOTE, not the corridor catalog: `isCrossCurrency` is literally
      // `currency.from !== currency.to`, so `currency.to` is present exactly when this branch runs, whereas
      // `destination` is a `countries.find` that can miss and would leave the sentence naming no wallet at
      // all — "the money is available in your  wallet". The catalog stays as the fallback it already was.
      note: fees?.isCrossCurrency
        ? t("result_rejected_note_converted", {
            currency: successData.quoteData?.currency.to ?? destination?.currency ?? "",
          })
        : t("result_rejected_note"),
    },
  }[outcome];

  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions, insecure context) — the reference is still readable on the button.
    }
  };

  return (
    <div className="flex animate-fade-up animate-duration-250 flex-col motion-reduce:animate-none">
      {/* Issuer and stamp: the two things a receipt states before anything else. */}
      <div className="flex items-center justify-between gap-4 px-6 pt-5">
        <ColocaLogo variant="black" className="h-5 dark:hidden [&>img]:h-full [&>img]:w-auto" />
        <ColocaLogo variant="white" className="hidden h-5 dark:block [&>img]:h-full [&>img]:w-auto" />
        <ColocaStatusPill variant={chip.variant}>{chip.label}</ColocaStatusPill>
      </div>

      <div className="flex flex-col items-center gap-3 px-8 pt-5 pb-7 text-center">
        {outcome === "pending" && pendingVisual ? (
          <div className="w-full max-w-md pb-1">{pendingVisual}</div>
        ) : (
          <div
            className={cn(
              "flex size-14 animate-jump-in animate-duration-400 items-center justify-center rounded-full motion-reduce:animate-none",
              headline.ring,
            )}
          >
            {headline.icon}
          </div>
        )}
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-bold tracking-tight text-foreground outline-none"
        >
          {headline.title}
        </h2>
        {/* The figure is the amount addressed to the recipient, not the debit — what the sender is checking
            is what the other person gets. The debit lives in the stub, where a cross-currency send makes the
            two differ. Mono rather than larger: it is a different KIND of fact from the headline, so it is
            set apart by typeface instead of competing with it on size. */}
        <p
          className={cn(
            "font-mono text-[32px] leading-none font-bold tracking-tight tabular-nums",
            outcome === "rejected" ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {format(received, destination?.currency ?? "")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("result_receipt_to")}{" "}
          <span className="font-semibold text-foreground">{successData.recipientDisplay}</span>
        </p>
        {/* Live because this screen resolves on its own: the settlement webhook can flip the outcome while
            the receipt is open, and a sighted user sees the glyph and the stamp change while a screen reader
            would otherwise be told nothing. This sentence is the plain-language version of the state, so it
            is the one worth announcing. */}
        <p aria-live="polite" className="max-w-[360px] text-sm leading-relaxed text-muted-foreground">
          {headline.note}
        </p>
        {outcome === "pending" && (
          <p className="max-w-[360px] text-xs text-muted-foreground">{t("result_pending_live_hint")}</p>
        )}
        {/* Sits with the recipient rather than in the action row below: it is an offer about the person who
            was just paid, not a decision about what to do next. */}
        {saveDraft && (
          <div className="pt-1">
            <SaveRecipientAction draft={saveDraft} withdrawn={outcome === "rejected"} />
          </div>
        )}
      </div>

      {/* The stub. A dashed rule and a tinted band are the whole perforation: cut here, keep this part. Two
          half-circle notches at the card edges were tried and removed — the card's own border keeps painting
          across them, so they read as a rendering seam rather than a cut, and the dashed rule already carries
          the idea on its own. */}
      <div className="border-t border-dashed border-border bg-muted/40 px-6 pt-5 pb-4">
        <div className="flex flex-col gap-2.5">
          {/* The reference is a copy button and not static text for the same reason it is on the screen at
              all: it is the string the sender quotes when they write to support about this payout. */}
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="shrink-0 text-muted-foreground">{t("result_reference_label")}</span>
            <button
              type="button"
              onClick={handleCopyReference}
              aria-label={copied ? t("result_reference_copied") : t("result_reference_copy")}
              className="inline-flex min-w-0 items-center gap-1.5 rounded-md font-mono text-sm font-semibold tracking-wide text-foreground transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <span className="truncate">{reference}</span>
              {copied ? (
                <Check className="size-3.5 shrink-0 text-success" aria-hidden="true" />
              ) : (
                <Copy className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
            </button>
          </div>

          {showDetails && (
            <div className="flex animate-fade-up animate-duration-200 flex-col gap-2.5 motion-reduce:animate-none">
              <StubRow
                label={t("route_label")}
                value={`${originCurrency} → ${destination?.name ?? successData.formData.destination}`}
              />
              <StubRow
                label={t("send_label")}
                value={<span className="tabular-nums">{format(send, originCurrency)}</span>}
              />
              {/* Two fees, two rows, never one. They are charged in DIFFERENT currencies — conversion in the
                  origin, the dispersion commission in the destination — so they cannot be added, and a
                  ternary that renders whichever it finds first does not choose between them, it HIDES one.
                  On a cross-currency send the quote always carries both (the wizard always quotes with
                  `country` + `rail`), so the commission was invisible on every one of them and the stub
                  stopped reconciling: send minus fee did not explain the headline, and the implied rate was
                  not the rate on the quote. These are the same two rows, under the same two labels, that
                  `QuoteBreakdown` showed on the screen the sender approved a moment earlier — a receipt that
                  contradicts the confirmation is worse than one that leaves the figure out. */}
              {!fees?.conversion && !fees?.payout && (
                <StubRow label={t("fees_label")} value={<span className="tabular-nums">—</span>} />
              )}
              {fees?.conversion && (
                <StubRow
                  label={t("quote_conversion_fee_label")}
                  value={
                    <span className="tabular-nums">
                      {format(fees.conversion.total, fees.conversion.currency)}
                    </span>
                  }
                />
              )}
              {fees?.payout && (
                <StubRow
                  label={t("quote_dispersion_fee_label")}
                  value={
                    <span className="tabular-nums">{format(fees.payout.total, fees.payout.currency)}</span>
                  }
                />
              )}
              {fees?.payout &&
                fees.taxes.map((tax) => (
                  <StubRow
                    key={tax.code}
                    label={taxLabel(tax)}
                    value={<span className="tabular-nums">{format(tax.amount, fees.payout!.currency)}</span>}
                  />
                ))}
              <StubRow label={t("concept_label")} value={concept} />
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onToggleDetails}
          aria-expanded={showDetails}
          className="mt-3 rounded-md text-sm font-semibold text-primary transition-colors hover:text-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {showDetails ? t("result_hide_details") : t("result_show_details")}
        </button>
      </div>

      {/* "Otra persona" is the primary and "nuevamente" is not, and that is the risk talking rather than a
          guess at frequency: repeating a send keeps the recipient AND the amount, so a mis-click there is a
          second payment to the same account. The safe one gets the emphasis.

          And while the payout is still unresolved, repeating is not offered at all. `POST /payouts` answers
          `PROCESSING` for every rail — the provider accepted it, the network confirms later — so the window
          in which this screen says "en proceso" is exactly the window in which somebody doubts the send went
          out. The button in reach at that moment rebuilds the wizard with a fresh quote and a fresh
          idempotency key, which is a genuinely NEW operation the backend will not deduplicate against the
          first. It is not a one-click double payment (the restart lands on step 1 and the three steps have
          to be walked again), but it is the wrong affordance to hand someone at the one moment it reads as
          "retry". The receipt resolves itself over the socket, so the hold lasts seconds and lifts on its
          own. The reason is rendered as text rather than a `title`, which a disabled control never announces
          and a touch screen cannot reach. */}
      {/* Two tiers, split by RISK rather than by guessed frequency. The first is the comprobante — see it,
          share it as an image or a PDF — which cannot move money; the second keeps the two buttons that can
          at their original weight, with leaving for the wallets as a `ghost` pushed to the far side.
          Flattening them into one tier would put a no-op beside a second payment at the same visual volume.

          Both tiers lay out against the RECEIPT's width (`@container`), never the viewport's: the send panel
          is a 640px column on any desktop, so a viewport breakpoint put the four utility buttons on one
          unbreakable line there (~650px in Spanish, ~750px in Portuguese) and the drawer's scroller cut
          "Ver comprobante" off its left edge. Narrow, the comprobante pairs into a 2-column grid (the shape
          `PayinQrCard` uses for Share/Download) and the decision buttons stack full width with the primary
          last; wide, the three comprobante buttons share one row in equal columns, and the decision row
          wraps rather than overflows when a locale's labels do not fit beside each other. */}
      <div className="@container flex flex-col gap-3 border-t border-border px-6 py-4">
        <div className="grid grid-cols-2 gap-2 @xl:grid-cols-3">
          <TransferReceiptActions
            payoutId={successData.dispersionResponse.id}
            status={status}
            organizationName={organizationName}
            className="contents"
            viewClassName="col-span-2 @xl:col-span-1"
          />
        </div>

        <div className="flex flex-col gap-2.5 @xl:flex-row @xl:flex-wrap @xl:items-center @xl:justify-end">
          {/* `Wallet`, not an arrow: the icon names where the button goes. And `ghost` rather than `link`,
              which has no height of its own and would break the row's touch targets. */}
          <ColocaButton
            variant="ghost"
            size="lg"
            icon={Wallet}
            className="px-4 @xl:mr-auto"
            onClick={onBackToWallets}
          >
            {t("result_back_to_wallets")}
          </ColocaButton>
          {/* Above the buttons it explains on a wide receipt, next to the disabled one on a narrow one. */}
          {repeatHeld && (
            <p
              id={repeatHintId}
              className="text-xs text-muted-foreground @xl:order-first @xl:basis-full @xl:text-right"
            >
              {t("result_send_again_pending_hint")}
            </p>
          )}
          {/* The two glyphs are load-bearing, not decoration: "repeat to the same person" and "send to
              someone new" are the pair that must never be mistaken for each other, and an icon gives that
              distinction a second channel beyond the primary/default contrast. They wrap as one group, so a
              row too narrow for all three drops the pair to the next line together instead of splitting it. */}
          <div className="flex flex-col gap-2.5 @xl:flex-row">
            <ColocaButton
              variant="default"
              size="lg"
              icon={RotateCw}
              className="px-4"
              onClick={onSendAgain}
              disabled={repeatHeld}
              aria-describedby={repeatHeld ? repeatHintId : undefined}
            >
              {t("result_send_again")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              size="lg"
              icon={UserPlus}
              className="px-4"
              onClick={onSendToSomeoneElse}
            >
              {t("result_send_to_someone_else")}
            </ColocaButton>
          </div>
        </div>
      </div>
    </div>
  );
}
